import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Menu, OctagonX, Search, Wand2 } from 'lucide-react';
import { ClusterSelector } from './ClusterSelector';
import { FilaSelector } from './FilaSelector';
import { UserMenu } from './UserMenu';
import { useEscalarModal } from '../modals/EscalarModalProvider';
import { useClusterStore } from '../../stores/clusterStore';
import { useBulkStore, type BulkSection } from '../../stores/bulkStore';
import { useSelectedIdStore } from '../../stores/selectedIdStore';
import { getDashboardSummary } from '../../lib/api';
import { formatBR } from '../../lib/utils';

const SECTION_LABELS: Record<string, string> = {
  overview: 'Overview',
  services: 'Serviços',
  nodes: 'Nodes',
  activity: 'Atividade',
  mosaic: 'Mosaic',
  search: 'Busca',
};

function sectionFromPath(pathname: string): string {
  const seg = pathname.split('/').filter(Boolean)[0] ?? 'overview';
  return seg;
}

export interface InspectorHeaderProps {
  onMenuClick: () => void;
}

export function InspectorHeader({ onMenuClick }: InspectorHeaderProps) {
  const location = useLocation();
  const section = sectionFromPath(location.pathname);
  const label = SECTION_LABELS[section] ?? section;
  const { openEscalar } = useEscalarModal();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // O input é a fonte da verdade enquanto se digita, mas precisa refletir a URL
  // quando ela muda por fora (link, voltar do navegador, busca recarregada).
  const [globalSearch, setGlobalSearch] = useState(searchParams.get('q') ?? '');
  useEffect(() => {
    if (section === 'search') setGlobalSearch(searchParams.get('q') ?? '');
  }, [searchParams, section]);
  const activeSlug = useClusterStore(s => s.activeSlug);
  const stackFilter = useClusterStore(s => s.stackFilter);
  const setStackFilter = useClusterStore(s => s.setStackFilter);

  const { data: summary } = useQuery({
    queryKey: ['dashboard', activeSlug, ''],
    queryFn: () => getDashboardSummary(activeSlug, ''),
    refetchInterval: 30_000,
  });
  const filaOptions = summary?.breakdowns?.byStack ?? [];
  const showFilaFilter =
    section === 'overview' && activeSlug === 'all' && filaOptions.length > 0;
  const totals = summary?.totals;
  const totalMachines = totals?.currentMachines ?? 0;
  const capacity = totals?.totalMachines ?? 0;
  const nodesOnline = totals?.nodesOk ?? 0;
  const nodesTotal = totals ? totals.nodesOk + totals.nodesDown : 0;
  const servicesCount = totals?.services ?? 0;

  const handleEscalar = (): void => {
    const sec = section as BulkSection;
    const selection = useBulkStore.getState().selections[sec];
    const resolver = useSelectedIdStore.getState().resolvers[sec];
    const ids = selection ? Array.from(selection) : [];
    const items = resolver ? resolver(ids) : [];
    openEscalar({
      type: 'bulk',
      cluster: activeSlug === 'all' ? '' : activeSlug,
      items,
    });
  };

  // Destrutivo e por cluster: só onde os serviços estão na tela e nunca em
  // "Todos", onde um clique derrubaria a frota inteira.
  const canResetAll = section === 'services' && activeSlug !== 'all';
  const handleResetAll = (): void => {
    openEscalar({ type: 'reset', cluster: activeSlug, items: [] });
  };

  return (
    <header className="flex h-11 flex-shrink-0 items-center gap-2.5 border-b border-border bg-surface px-2.5 sm:px-3.5">
      <button
        type="button"
        onClick={onMenuClick}
        aria-label="Abrir navegação"
        className="inline-flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-textMute hover:bg-surfaceAlt hover:text-text md:hidden"
      >
        <Menu size={17} />
      </button>

      <div className="flex items-center gap-2">
        <h1 className="text-sm font-bold tracking-tight text-text">{label}</h1>
      </div>

      <div aria-hidden className="ml-1.5 hidden h-[18px] w-px bg-border sm:block" />

      <ClusterSelector />

      {showFilaFilter && (
        <FilaSelector
          value={stackFilter}
          options={filaOptions}
          onChange={setStackFilter}
        />
      )}

      <div className="hidden items-center gap-2 whitespace-nowrap font-mono text-[11.5px] text-textMute md:flex">
        <span className="text-[9px] text-ok">●</span>
        <span>
          <span className="font-semibold text-text">
            {formatBR(totalMachines)}/{formatBR(capacity)}
          </span>{' '}
          máq ·{' '}
          <span className="font-semibold text-text">
            {formatBR(servicesCount)}
          </span>{' '}
          serv ·{' '}
          <span className="font-semibold text-text">
            {formatBR(nodesOnline)}/{formatBR(nodesTotal)}
          </span>{' '}
          nodes
        </span>
      </div>

      <div className="flex-1" />

      <form
        role="search"
        onSubmit={e => {
          e.preventDefault();
          const term = globalSearch.trim();
          if (term) navigate(`/search?q=${encodeURIComponent(term)}`);
        }}
        className="relative hidden items-center lg:flex"
      >
        <Search
          size={11}
          className="pointer-events-none absolute left-2 text-textSoft"
        />
        <input
          type="search"
          value={globalSearch}
          onChange={e => setGlobalSearch(e.target.value)}
          placeholder="Buscar serviço, host, IP ou container em todos os flows…"
          aria-label="Buscar em todos os clusters"
          className="h-7 w-[260px] rounded-md border border-border bg-bg pl-6 pr-2 font-mono text-[11px] text-text placeholder:text-textSoft focus:border-accent focus:outline-none"
        />
      </form>

      {canResetAll && (
        <button
          type="button"
          onClick={handleResetAll}
          aria-label="Zerar todos os serviços ativos"
          title="Zerar todos os serviços ativos"
          className="inline-flex h-7 items-center gap-1.5 rounded-md bg-danger px-2.5 text-[11.5px] font-medium text-white hover:bg-danger/90"
        >
          <OctagonX size={11} />
          <span className="hidden xl:inline">Zerar todos os serviços ativos</span>
        </button>
      )}

      <button
        type="button"
        onClick={handleEscalar}
        aria-label="Escalar máquinas"
        className="inline-flex h-7 items-center gap-1.5 rounded-md bg-accent px-2.5 text-[11.5px] font-medium text-white hover:bg-primary"
      >
        <Wand2 size={11} />
        <span className="hidden sm:inline">Escalar máquinas</span>
      </button>

      <div aria-hidden className="hidden h-[18px] w-px bg-border sm:block" />

      <UserMenu />
    </header>
  );
}
